import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import swal from 'sweetalert2';
import { BiX } from 'react-icons/bi';
import Loading from '../common/Loading';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  width: 640px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    width: 92vw;
    padding: 20px;
  }
`;

const CloseBtn = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;

const Title = styled.h3`
  margin-bottom: 24px;
  font-size: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
`;

const Label = styled.div`
  width: 130px;
  font-weight: 600;
  color: #555;
  flex-shrink: 0;
  font-size: 15px;
`;

const Value = styled.div`
  flex: 1;
  font-size: 15px;
`;

const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  th, td {
    padding: 10px 12px;
    text-align: center;
    border-bottom: 1px solid #eee;
    font-size: 14px;
  }
  thead tr {
    background: #2d3748;
    color: #fff;
  }
`;

const SectionTitle = styled.div`
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 8px;
  font-size: 16px;
`;

const DeleteBtn = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #fef2f2;
  color: #dc2626;
  transition: all 0.15s;
  &:hover { background: #fee2e2; }
`;

const CommentSection = styled.div`
  margin-top: 24px;
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
`;

const CommentItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
`;

const CommentText = styled.div`
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CommentTime = styled.div`
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CommentDelete = styled.span`
  font-size: 12px;
  color: #d1d5db;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { color: #ef4444; }
`;

const CommentInput = styled.div`
  display: flex;
  gap: 8px;
`;

const CommentTextarea = styled.textarea`
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: none;
  min-height: 40px;
  line-height: 1.5;
  &:focus { border-color: #007aff; }
`;

const CommentBtn = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #007aff;
  color: #fff;
  white-space: nowrap;
  align-self: flex-end;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const InlineInput = styled.input`
  padding: 5px 10px;
  border: 1.5px solid #d2d2d7;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  flex: 1;
  transition: border-color 0.15s;
  &:focus { border-color: #007aff; }
`;

const SaveBtn = styled.button`
  padding: 5px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #007aff;
  color: #fff;
  white-space: nowrap;
  &:hover { opacity: 0.85; }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $status }) =>
    $status === 'pending' ? '#fef3c7' :
    $status === 'preparing' ? '#dbeafe' :
    '#ecfdf5'};
  color: ${({ $status }) =>
    $status === 'pending' ? '#92400e' :
    $status === 'preparing' ? '#1e40af' :
    '#065f46'};
`;

const getOrderStatus = (postNum) => {
  if (!postNum || postNum === '발주 확인 중') return 'pending';
  if (postNum === '출고 준비' || postNum === '출고준비') return 'preparing';
  return 'shipped';
};

const statusLabel = { pending: '확인중', preparing: '출고 준비', shipped: '출고완료' };

const OrderDetailModal = ({ orderId, onClose, onDeleted }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editPostNum, setEditPostNum] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`/admin/users/orderlist/detail/${orderId}`);
        setOrder(data);
        setEditPostNum(data.postNum || '');
        setComments(data.admin_comments || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [orderId]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // 모달 열릴 때 부모 스크롤 방지
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalBox>
        <CloseBtn onClick={onClose}><BiX size={28} /></CloseBtn>
        {loading ? (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loading />
          </div>
        ) : !order ? (
          <div>주문 정보를 불러올 수 없습니다.</div>
        ) : (
          <>
            <Title>주문 상세</Title>
            <InfoRow>
              <Label>발주번호</Label>
              <Value>{order.order_id}</Value>
            </InfoRow>
            <InfoRow>
              <Label>회사</Label>
              <Value>{order.company_name}</Value>
            </InfoRow>
            <InfoRow>
              <Label>발주일시</Label>
              <Value>{order.time ? order.time.replace(/(\d{4})\.(\d{2})\.(\d{2})-?(\d{2}):(\d{2}):(\d{2})/, (_, y, m, d, h, mi, s) => `${y.slice(2)}/${m}/${d} ${h}:${mi}:${s}`) : '-'}</Value>
            </InfoRow>
            {(order.shipping_name || order.shipping_phone || order.shipping_address) && (
              <InfoRow>
                <Label>배송지</Label>
                <Value>
                  {order.shipping_name && <div>{order.shipping_name} {order.shipping_phone && `/ ${order.shipping_phone}`}</div>}
                  {order.shipping_address && <div style={{ color: '#555', fontSize: 14 }}>{order.shipping_address}</div>}
                </Value>
              </InfoRow>
            )}
            <InfoRow>
              <Label>상태</Label>
              <Value>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={async () => {
                    const { value } = await swal.fire({
                      title: '상태 변경',
                      input: 'select',
                      inputOptions: { '발주 확인 중': '확인중', '출고 준비': '출고 준비', '': '출고완료 (운송장 직접 입력)' },
                      inputValue: order.postNum === '발주 확인 중' ? '발주 확인 중' : (order.postNum === '출고 준비' || order.postNum === '출고준비') ? '출고 준비' : '',
                      confirmButtonText: '변경',
                      cancelButtonText: '취소',
                      confirmButtonColor: '#007aff',
                      cancelButtonColor: '#a3a3a3',
                      showCancelButton: true,
                    });
                    if (value === undefined) return;
                    if (value === '') {
                      setIsEditing(true);
                      return;
                    }
                    try {
                      await axios.patch(`/admin/users/orderlist/${orderId}`, { postNum: value });
                      setOrder({ ...order, postNum: value });
                      setEditPostNum(value);
                      if (onDeleted) onDeleted();
                      swal.fire({ text: '변경되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                    } catch {
                      swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
                    }
                  }}>
                  <StatusBadge $status={getOrderStatus(order.postNum)}>
                    {statusLabel[getOrderStatus(order.postNum)]}
                  </StatusBadge>
                  <span style={{ fontSize: 12, color: '#007aff', fontWeight: 600 }}>편집</span>
                </div>
              </Value>
            </InfoRow>
            <InfoRow>
              <Label>운송장 번호</Label>
              <Value>
                {isEditing ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <InlineInput
                      value={editPostNum === '발주 확인 중' || editPostNum === '출고 준비' ? '' : editPostNum}
                      onChange={(e) => setEditPostNum(e.target.value)}
                      placeholder="택배사 + 운송장번호"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && editPostNum.trim()) {
                          axios.patch(`/admin/users/orderlist/${orderId}`, { postNum: editPostNum.trim() })
                            .then(() => {
                              setOrder({ ...order, postNum: editPostNum.trim() });
                              setIsEditing(false);
                              if (onDeleted) onDeleted();
                              swal.fire({ text: '저장되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                            })
                            .catch(() => swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 }));
                        } else if (e.key === 'Escape') {
                          setEditPostNum(order.postNum || '');
                          setIsEditing(false);
                        }
                      }}
                    />
                    <SaveBtn onClick={async () => {
                      if (!editPostNum.trim()) return;
                      try {
                        await axios.patch(`/admin/users/orderlist/${orderId}`, { postNum: editPostNum.trim() });
                        setOrder({ ...order, postNum: editPostNum.trim() });
                        setIsEditing(false);
                        if (onDeleted) onDeleted();
                        swal.fire({ text: '저장되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                      } catch {
                        swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
                      }
                    }}>저장</SaveBtn>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span>{order.postNum || '-'}</span>
                    <span
                      style={{ fontSize: 12, color: '#007aff', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setIsEditing(true)}
                    >편집</span>
                  </div>
                )}
              </Value>
            </InfoRow>
            {order.verified_price != null && (
              <InfoRow>
                <Label>주문 총액</Label>
                <Value>{order.verified_price.toLocaleString('ko-KR')}원</Value>
              </InfoRow>
            )}
            {order.items && order.items.length > 0 ? (
              <>
                <SectionTitle>주문 상품</SectionTitle>
                <ItemTable>
                  <thead>
                    <tr>
                      <th>상품명</th>
                      <th>수량</th>
                      <th>단가</th>
                      <th>소계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: 'left' }}>
                          {item.product_name}
                          {item.product_id && (
                            <a
                              href={`/product/list/detail/${item.product_id}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ marginLeft: 6, fontSize: 13, textDecoration: 'none' }}
                              title="상품 상세"
                            >&#x1F517;</a>
                          )}
                        </td>
                        <td>{item.count}</td>
                        <td>{item.unit_price?.toLocaleString('ko-KR')}원</td>
                        <td>{(item.count * item.unit_price).toLocaleString('ko-KR')}원</td>
                      </tr>
                    ))}
                  </tbody>
                  {order.shipping_fee > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'right', fontWeight: 600, color: '#555' }}>배송비</td>
                        <td style={{ fontWeight: 600 }}>{order.shipping_fee.toLocaleString('ko-KR')}원</td>
                      </tr>
                    </tfoot>
                  )}
                </ItemTable>
              </>
            ) : (
              <>
                <SectionTitle>주문 상품</SectionTitle>
                <div style={{ color: '#999', fontSize: 13 }}>
                  상품 상세 정보가 없습니다. (이전 주문)
                </div>
              </>
            )}

            <CommentSection>
              <SectionTitle>관리자 메모</SectionTitle>
              {comments.length > 0 && (
                <CommentList>
                  {comments.map((c) => (
                    <CommentItem key={c._id}>
                      <CommentText>{c.text}</CommentText>
                      <CommentTime>{c.created_at}</CommentTime>
                      <CommentDelete onClick={async () => {
                        try {
                          const { data } = await axios.delete(`/admin/users/orderlist/comment/${orderId}/${c._id}`);
                          setComments(data);
                        } catch {
                          swal.fire({ text: '삭제 실패', icon: 'error', showConfirmButton: false, timer: 1000 });
                        }
                      }}>&times;</CommentDelete>
                    </CommentItem>
                  ))}
                </CommentList>
              )}
              <CommentInput>
                <CommentTextarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="메모를 입력하세요..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (newComment.trim()) {
                        axios.post(`/admin/users/orderlist/comment/${orderId}`, { text: newComment })
                          .then(({ data }) => { setComments(data); setNewComment(''); })
                          .catch(() => swal.fire({ text: '오류 발생', icon: 'error', showConfirmButton: false, timer: 1000 }));
                      }
                    }
                  }}
                />
                <CommentBtn
                  disabled={!newComment.trim()}
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`/admin/users/orderlist/comment/${orderId}`, { text: newComment });
                      setComments(data);
                      setNewComment('');
                    } catch {
                      swal.fire({ text: '오류 발생', icon: 'error', showConfirmButton: false, timer: 1000 });
                    }
                  }}
                >등록</CommentBtn>
              </CommentInput>
            </CommentSection>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <DeleteBtn onClick={async () => {
                const step1 = await swal.fire({
                  text: '이 주문을 삭제하시겠습니까?',
                  icon: 'warning',
                  iconColor: '#dc2626',
                  showCancelButton: true,
                  confirmButtonColor: '#dc2626',
                  cancelButtonColor: '#a3a3a3',
                  confirmButtonText: '삭제',
                  cancelButtonText: '취소',
                });
                if (!step1.isConfirmed) return;
                const step2 = await swal.fire({
                  text: '정말 삭제하시겠습니까? 휴지통에서 복원할 수 있습니다.',
                  icon: 'warning',
                  iconColor: '#dc2626',
                  showCancelButton: true,
                  confirmButtonColor: '#dc2626',
                  cancelButtonColor: '#a3a3a3',
                  confirmButtonText: '삭제 확인',
                  cancelButtonText: '취소',
                });
                if (!step2.isConfirmed) return;
                try {
                  await axios.patch(`/admin/trash/delete/order/${orderId}`);
                  swal.fire({ text: '삭제되었습니다.', icon: 'success', showConfirmButton: false, timer: 1000 });
                  if (onDeleted) onDeleted();
                  onClose();
                } catch {
                  swal.fire({ text: '오류가 발생했습니다.', icon: 'error', showConfirmButton: false, timer: 1000 });
                }
              }}>
                주문 삭제
              </DeleteBtn>
            </div>
          </>
        )}
      </ModalBox>
    </Overlay>
  );
};

export default OrderDetailModal;
